"use client";

/**
 * HeroComponent
 * Hero section for landing pages
 */

import { HeroConfig, LandingPageComponent } from "@/lib/database/landing-pages";
import { Calendar, MapPin } from "lucide-react";
import { useMemo } from "react";
import { DEFAULT_PRIMARY_COLOR, generateColorPalette } from "../../../_logic/color-utils";
import { DEFAULT_VALUES } from "../../../_logic/constants";
import SmartButton from "../../shared/SmartButton";

interface HeroComponentProps {
  component: LandingPageComponent;
  eventId: number;
  primaryColor?: string;
}

export default function HeroComponent({ component, eventId, primaryColor }: HeroComponentProps) {
  const config = (component.config || {}) as HeroConfig;

  const title = config.title || DEFAULT_VALUES.HERO_TITLE;
  const date = config.date || "";
  const location = config.location || "";
  const description = config.description || "";
  const backgroundImage = config.backgroundImage || "";
  const backgroundImageOverlay = config.backgroundImageOverlay || false;

  // Support both old single CTA and new multiple CTAs
  const ctaButtons = useMemo(() => {
    if (config.ctaButtons && config.ctaButtons.length > 0) {
      return config.ctaButtons;
    }
    // Backwards compatibility: convert old ctaText/ctaLink to ctaButtons format
    if (config.ctaText && config.ctaLink) {
      return [{ text: config.ctaText, link: config.ctaLink }];
    }
    return [];
  }, [config.ctaButtons, config.ctaText, config.ctaLink]);

  // Generate color palette from primary color
  const palette = useMemo(() => generateColorPalette(primaryColor || DEFAULT_PRIMARY_COLOR), [primaryColor]);

  const backgroundStyle = useMemo(() => {
    if (backgroundImage && backgroundImageOverlay) {
      // Overlay mode: background color gradient with image overlay
      return {
        background: `linear-gradient(135deg, ${palette.gradient.from} 0%, ${palette.primary} 50%, ${palette.gradient.to} 100%)`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      };
    } else if (backgroundImage) {
      // Default mode: image with dark overlay
      return {
        background: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.6)), url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      };
    } else {
      // No image: just gradient
      return {
        background: `linear-gradient(135deg, ${palette.gradient.from} 0%, ${palette.primary} 50%, ${palette.gradient.to} 100%)`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      };
    }
  }, [backgroundImage, backgroundImageOverlay, palette]);

  return (
    <div
      id="hero"
      className="relative text-white min-h-[85vh] flex items-center justify-center overflow-hidden"
      style={backgroundStyle}
    >
      {backgroundImage && backgroundImageOverlay && (
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      )}
      <div className="relative z-10 max-w-5xl mx-auto px-4 py-24 sm:py-32">
        <div className="text-center">
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black mb-6 tracking-tight">{title}</h1>
          {(date || location) && (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 mb-6 text-lg md:text-xl">
              {date && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <span className="font-semibold">{date}</span>
                </div>
              )}
              {date && location && <span className="hidden sm:inline">•</span>}
              {location && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  <span className="font-semibold">{location}</span>
                </div>
              )}
            </div>
          )}
          {description && (
            <p className="text-lg md:text-xl mb-12 max-w-3xl mx-auto opacity-95 font-medium whitespace-pre-line">
              {description}
            </p>
          )}
          {ctaButtons.length > 0 && (
            <div className="flex flex-wrap justify-center gap-4">
              {ctaButtons.map((cta, index) => (
                <SmartButton
                  key={index}
                  href={cta.link}
                  text={cta.text}
                  primaryColor={palette.primary}
                  eventId={eventId}
                  className="px-8 py-4 text-lg shadow-lg"
                  style={{
                    backgroundColor: "white",
                    color: palette.primary,
                  }}
                  nextSteps={cta.nextSteps}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
